/**
 * File: businesses.ts
 * Path: packages/config/src/businesses/businesses.ts
 * Description: One record per business (data tidy, Liam 3 Nov 2026).
 *
 * A lead and the client it became are the same business, so they become one
 * record in `businesses/{id}` with a `stage` that runs lead to won to past.
 * The functions here are pure: they turn what is stored in `leads/{id}` and
 * `clients/{id}` today into the one record, and decide its readable folder
 * name. Nothing here reads or writes the database.
 */

export const BUSINESSES_COLLECTION = "businesses";

/** Today's lead stages, plus "past" for a former client. */
export const BUSINESS_STAGES = [
  "new",
  "contacted",
  "talking",
  "quoted",
  "won",
  "lost",
  "past",
] as const;
export type BusinessStage = (typeof BUSINESS_STAGES)[number];

type Raw = Record<string, unknown>;

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/**
 * The stage of the merged business. A lead keeps its pipeline stage; a client
 * with no lead is won; a client set to "past" is past; a client set to "lead"
 * is a lead who was added in the clients list by hand.
 */
export const businessStageOf = (lead: Raw | null, client: Raw | null): BusinessStage => {
  if (client?.type === "past") return "past";
  const leadStage = text(lead?.stage);
  if (leadStage && (BUSINESS_STAGES as readonly string[]).includes(leadStage)) {
    // A client exists, so the work was won, whatever the lead said last.
    if (client && leadStage !== "won") return client.type === "lead" ? "new" : "won";
    return leadStage as BusinessStage;
  }
  if (client) return client.type === "lead" ? "new" : "won";
  return "new";
};

/** Fields that exist only on the lead or only on the client keep their names. */
const KEEP_FROM_CLIENT_OVER_LEAD = [
  "name",
  "contactName",
  "phone",
  "email",
  "website",
  "address",
  "niche",
  "value",
  "doNotContact",
  "marketingConsent",
  "consent",
  "contacts",
  "branches",
] as const;

const isBlank = (v: unknown) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * The stored business: every lead field, then every client field on top
 * (a client's detail is the later, more careful one), without losing a value
 * that only the lead has. `legacy` records where it came from.
 */
export const mergeBusinessRecord = (
  lead: { id: string; data: Raw } | null,
  client: { id: string; data: Raw } | null,
): Raw => {
  const out: Raw = { ...(lead?.data ?? {}) };
  for (const [key, value] of Object.entries(client?.data ?? {})) {
    if (key === "leadId" || key === "type") continue;
    if (isBlank(value) && !isBlank(out[key]) && (KEEP_FROM_CLIENT_OVER_LEAD as readonly string[]).includes(key)) continue;
    out[key] = value;
  }
  out.stage = businessStageOf(lead?.data ?? null, client?.data ?? null);
  if (client && client.data.type === "partner") out.partner = true;
  out.legacy = { leadId: lead?.id ?? null, clientId: client?.id ?? null };
  return out;
};

const slug = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "") || "business";

/**
 * The business's folder name in Storage: C002-sir-bastians for a client,
 * lead-sir-bastians-ab12cd for a lead. Fixed when the record is first made.
 */
export const businessFolderName = (input: {
  id: string;
  name: string;
  clientNumber: number | null;
}): string =>
  input.clientNumber !== null && input.clientNumber >= 1
    ? `C${String(input.clientNumber).padStart(3, "0")}-${slug(input.name)}`
    : `lead-${slug(input.name)}-${input.id.replace(/[^a-zA-Z0-9]/g, "").slice(-6).toLowerCase()}`;
